/** Execution key handling, attachments and screenshot capture for the layout. */
import type { ImageAttachment } from "@/components/panels/image-preview-strip";
import {
  attachComposerFile,
  removeComposerAttachment,
} from "@/lib/composer-attachment";
import { displayRosterToken } from "@/desktop/display-roster-token";
import {
  registerOutboundScreensForImages,
  setOutboundScreens,
} from "@/desktop/outbound-screenshot-metadata";
import { buildScreenshotPointerToken } from "@/desktop/screenshot-pointer";
import { useScreenshotSettings } from "@/desktop/screenshot-settings";
import {
  useScreenshotCapture,
  type CaptureQuality,
  type ScreenshotPreview,
} from "@/desktop/use-screenshot-capture";
import { modelSupportsVision } from "@/lib/model-vision";
import { stopCurrentAgentSession } from "@/lib/stop-agent-session";
import { useCompanionStore } from "@/stores/companion-store";
import {
  useCallback,
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

export function useMasterLayoutMedia({
  currentSpaceId,
  setAttachedImages,
}: {
  currentSpaceId: string | undefined;
  setAttachedImages: Dispatch<SetStateAction<ImageAttachment[]>>;
}) {
  const isStreaming = useCompanionStore((s) => {
    if (!currentSpaceId) return false;
    // Source of truth is the executions map. A stale `isAgentResponding`
    // flag (e.g. a terminal status broadcast we missed on WS reconnect,
    // or a heartbeat execution that cleaned up silently) used to keep
    // the stop button visible forever; checking the map directly means
    // the button disappears as soon as no execution in this space is
    // running. `isAgentResponding` is kept separately for the input bar
    // disable state, which is intentionally more conservative.
    return Array.from(s.executions.values()).some(
      (exec) =>
        exec.spaceId === currentSpaceId &&
        (exec.status === "running" || exec.status === "waiting_approval"),
    );
  });
  const handleStopExecution = useCallback(() => {
    void stopCurrentAgentSession();
  }, []);

  // Escape key stops the running execution (like Escape in Claude Code CLI)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      // Don't intercept Escape when typing in an input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (isStreaming) {
        handleStopExecution();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isStreaming, handleStopExecution]);

  // Auto-focus input bar when user starts typing and nothing else is focused
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.defaultPrevented ||
        (e.target as HTMLElement)?.closest('[data-testid="search-hub"]')
      )
        return;
      // Skip if already in an input, textarea, or contenteditable
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if ((e.target as HTMLElement)?.isContentEditable) return;
      // Skip modifier keys, function keys, navigation keys
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key.length !== 1) return; // only printable characters
      // Focus the input bar
      const input = document.querySelector<HTMLTextAreaElement>(
        '[data-testid="input-bar-field"]',
      );
      if (input) {
        input.focus();
        // Don't prevent default — the character will be typed into the now-focused input
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleImageFile = useCallback(
    async (
      file: File,
      _source: "paste" | "drop",
      _position?: { x: number; y: number },
    ): Promise<ImageAttachment | null> => {
      return attachComposerFile(file, setAttachedImages);
    },
    [setAttachedImages],
  );

  // Capture only for per-request intent, selection, or screen keywords.
  // Return uploaded attachments for inclusion in the same dispatch.
  const { captureNow } = useScreenshotCapture();
  const [screenshotIntent, setScreenshotIntent] = useState(false);
  const copyImagesToCanvas = useScreenshotSettings(
    (s) => s.copySentImagesToCanvas,
  );

  const captureAndAttachIfEnabled = useCallback(
    async (
      currentModelId: string | undefined,
      shouldCapture: boolean,
      scope: "cursor_screen" | "all" = "all",
      quality?: CaptureQuality,
    ): Promise<{
      images: ImageAttachment[];
      pointerToken: string | null;
      displaysToken: string | null;
      previews: ScreenshotPreview[];
    }> => {
      const isTauri =
        typeof window !== "undefined" &&
        Boolean(
          (window as unknown as { __TAURI_INTERNALS__?: object })
            .__TAURI_INTERNALS__,
        );
      if (!isTauri || !shouldCapture) {
        return {
          images: [],
          pointerToken: null,
          displaysToken: null,
          previews: [],
        };
      }
      if (!modelSupportsVision(currentModelId)) {
        return {
          images: [],
          pointerToken: null,
          displaysToken: null,
          previews: [],
        };
      }
      const {
        files,
        screens,
        previews = [],
        cursorX,
        cursorY,
      } = await captureNow({ scope, quality });
      // Stash per-screen metadata so we can invert any subsequent
      // [POINT:...] reply back to global desktop coords. Last-write-wins
      // — the inbound reply consumer reads it from this slot.
      setOutboundScreens(screens);
      const uploaded = await Promise.all(
        files.map((f) => handleImageFile(f, "drop")),
      );
      const successful = uploaded.flatMap((image, index) => {
        const screen = screens[index];
        return image && screen ? [{ image, screen }] : [];
      });
      const images = successful.map(({ image }) => image);
      const attachedScreens = successful.map(({ screen }, index) => ({
        ...screen,
        index,
      }));
      registerOutboundScreensForImages(
        images.map((img) => img.imageId).filter((id): id is string => !!id),
        attachedScreens,
      );
      const pointerToken = buildScreenshotPointerToken(
        attachedScreens,
        cursorX,
        cursorY,
      );
      // Only the captured display is attached. With more than one connected,
      // say which, or "screen=1 of 1" reads as "your desktop is one screen".
      const displaysToken = await displayRosterToken(
        attachedScreens.map((screen) => screen.displayId),
      );
      return { images, pointerToken, displaysToken, previews };
    },
    [captureNow, handleImageFile],
  );

  const handleRemoveImage = useCallback(
    (id: string) => {
      removeComposerAttachment(id, setAttachedImages);
    },
    [setAttachedImages],
  );

  return {
    isStreaming,
    handleStopExecution,
    handleImageFile,
    screenshotIntent,
    setScreenshotIntent,
    copyImagesToCanvas,
    captureAndAttachIfEnabled,
    handleRemoveImage,
  };
}
